import React, { useState, useEffect } from 'react';

import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const DateTimeSelector = ({ selectedDate, selectedTime, onDateChange, onTimeChange, errors }) => {
  const [showCalendar, setShowCalendar] = useState(false);

  const formatDate = (date) => {
    return date?.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Default to tomorrow on first render
  useEffect(() => {
    if (!selectedDate) {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      onDateChange(formatDate(tomorrow));
    }
  }, []);
  
  const timeSlots = [
    { id: '09:00', label: '9:00 AM - 11:00 AM', available: true },
    { id: '11:00', label: '11:00 AM - 1:00 PM', available: true },
    { id: '13:00', label: '1:00 PM - 2:00 PM (Lunch)', available: true },
    { id: '14:00', label: '2:00 PM - 4:00 PM', available: true },
    { id: '16:00', label: '4:00 PM - 6:00 PM', available: true },
    { id: '18:00', label: '6:00 PM - 8:00 PM', available: true },
    { id: '20:00', label: '8:00 PM - 10:00 PM', available: true }
  ];

  // Function to check if a time slot has passed for today
  const isTimeSlotPassed = (timeSlotId) => {
    if (!selectedDate) return false;
    
    const today = new Date();
    const selectedDateObj = new Date(selectedDate.split('/').reverse().join('-'));
    
    // Only check for today's date
    if (selectedDateObj.toDateString() !== today.toDateString()) {
      return false;
    }
    
    const currentTime = new Date();
    const currentHour = currentTime.getHours();
    const currentMinute = currentTime.getMinutes();
    
    // Parse the time slot ID (e.g., '09:00' -> 9:00)
    const slotHour = parseInt(timeSlotId.split(':')[0]);
    const slotMinute = parseInt(timeSlotId.split(':')[1]);
    
    // Convert current time to minutes for easier comparison
    const currentTimeInMinutes = currentHour * 60 + currentMinute;
    const slotTimeInMinutes = slotHour * 60 + slotMinute;
    
    // If current time is past the slot start time, disable it
    // For example: if current time is 6:40 PM (18:40 = 1120 minutes) and slot is 6:00 PM (18:00 = 1080 minutes)
    // Then 1120 >= 1080, so slot is disabled
    return currentTimeInMinutes >= slotTimeInMinutes;
  };

  const generateCalendarDays = () => {
    const today = new Date();
    const days = [];
    
    // Start from i = 1 to exclude today
    for (let i = 1; i <= 14; i++) {
      const date = new Date(today);
      date?.setDate(today?.getDate() + i);
      days?.push(date);
    }
    
    return days;
  };

  const formatDisplayDate = (date) => {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow?.setDate(today?.getDate() + 1);
    
    if (date?.toDateString() === today?.toDateString()) {
      return 'Today';
    } else if (date?.toDateString() === tomorrow?.toDateString()) {
      return 'Tomorrow';
    } else {
      return date?.toLocaleDateString('en-IN', {
        weekday: 'short',
        day: 'numeric',
        month: 'short'
      });
    }
  };

  const handleDateSelect = (date) => {
    onDateChange(formatDate(date));
    setShowCalendar(false);
    
    // Clear selected time if it's no longer valid for the new date
    if (selectedTime && isTimeSlotPassed(selectedTime)) {
      onTimeChange('');
    }
  };

  const calendarDays = generateCalendarDays();

  return (
    <div className="space-y-6">
      <div className="border-b border-border pb-4">
        <h3 className="text-lg font-semibold text-foreground mb-2">Pickup Schedule</h3>
        <p className="text-sm text-muted-foreground">Select your preferred pickup date and time</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-3">
          <label className="block text-sm font-medium text-foreground">
            Pickup Date <span className="text-destructive">*</span>
          </label>
          
          <div className="relative">
            <Button
              variant="outline"
              fullWidth
              onClick={() => setShowCalendar(!showCalendar)}
              className="justify-between h-12"
            >
              <span className={selectedDate ? 'text-foreground' : 'text-muted-foreground'}>
                {selectedDate || 'Select pickup date'}
              </span>
              <Icon name="Calendar" size={20} />
            </Button>
            
            {errors?.date && (
              <p className="text-sm text-destructive mt-1">{errors?.date}</p>
            )}
            
            {showCalendar && (
              <div className="absolute top-full left-0 right-0 z-10 mt-2 bg-card border border-border rounded-lg shadow-lg p-4">
                <div className="grid grid-cols-2 gap-2 max-h-64 overflow-y-auto">
                  {calendarDays?.map((date, index) => (
                    <Button
                      key={index}
                      variant={selectedDate === formatDate(date) ? 'default' : 'ghost'}
                      size="sm"
                      onClick={() => handleDateSelect(date)}
                      className="justify-start h-auto py-3"
                    >
                      <div className="text-left">
                        <div className="font-medium">{formatDisplayDate(date)}</div>
                        <div className="text-xs opacity-70">{formatDate(date)}</div>
                      </div>
                    </Button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
        
        <div className="space-y-3">
          <label className="block text-sm font-medium text-foreground">
            Pickup Time <span className="text-destructive">*</span>
          </label>
          
          <div className="space-y-2">
            {timeSlots?.map((slot) => {
              const isPassed = isTimeSlotPassed(slot?.id);
              const isDisabled = !slot?.available || isPassed;
              
              return (
                <Button
                  key={slot?.id}
                  variant={selectedTime === slot?.id ? 'default' : 'outline'}
                  fullWidth
                  disabled={isDisabled}
                  onClick={() => onTimeChange(slot?.id)}
                  className={`justify-between h-12 ${
                    isDisabled ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  <span>{slot?.label}</span>
                  {!slot?.available && (
                    <span className="text-xs text-destructive">Unavailable</span>
                  )}
                  {isPassed && slot?.available && (
                    <span className="text-xs text-destructive">Passed</span>
                  )}
                  {slot?.available && !isPassed && selectedTime === slot?.id && (
                    <Icon name="Check" size={16} />
                  )}
                </Button>
              );
            })}
          </div>
          
          {errors?.time && (
            <p className="text-sm text-destructive">{errors?.time}</p>
          )}
          
          {/* Show message when today is selected */}
          {selectedDate && (() => {
            const today = new Date();
            const selectedDateObj = new Date(selectedDate.split('/').reverse().join('-'));
            const isToday = selectedDateObj.toDateString() === today.toDateString();
            
            if (isToday) {
              const passedSlots = timeSlots.filter(slot => isTimeSlotPassed(slot.id));
              if (passedSlots.length > 0) {
                return (
                  <div className="bg-warning/10 border border-warning/20 rounded-lg p-3 mt-3">
                    <div className="flex items-start space-x-2">
                      <Icon name="Clock" size={16} className="text-warning mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-warning">Time Slot Notice</p>
                        <p className="text-xs text-warning/80 mt-1">
                          Some time slots have passed for today. Only available slots are shown.
                        </p>
                      </div>
                    </div>
                  </div>
                );
              }
            }
            return null;
          })()}
        </div>
      </div>
      <div className="bg-accent/10 border border-accent/20 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <Icon name="Info" size={20} className="text-accent mt-0.5" />
          <div>
            <h4 className="font-medium text-accent mb-1">Pickup Information</h4>
            <ul className="text-sm text-muted-foreground space-y-1">
              <li>• Our team will arrive within the selected time window</li>
              <li>• Please ensure someone is available to hand over the clothes</li>
              <li>• We provide free pickup and delivery within Coimbatore city limits</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DateTimeSelector;
